/**
 * @fileoverview Componente de breadcrumbs da aplicação.
 *
 * Exibe a hierarquia de navegação: Workspace > Projeto > Módulo > Página.
 * Mapeamento de rotas para labels em português (ROUTE_LABELS).
 * Visível apenas em telas desktop (hidden em mobile).
 */
import { useLocation, Link } from 'react-router-dom';
import { ChevronRight, Home, Building2, Kanban } from 'lucide-react';
import { useStore } from '@/store/useStore';
import { useShallow } from 'zustand/shallow';

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Dashboard',
  '/dashboard': 'Dashboard',
  '/highlights': 'Highlights',
  '/pipeline': 'Pipeline',
  '/leads': 'Leads',
  '/equipe': 'Equipe',
  '/clientes': 'Clientes',
  '/metas': 'Metas',
  '/whatsapp': 'WhatsApp',
  '/configuracoes': 'Configurações',
  '/workspaces': 'Workspaces',
  '/automacoes': 'Automações',
  '/integracoes': 'Integrações',
  '/atividades': 'Atividades',
  '/previsao': 'Forecast',
  '/relatorios': 'Relatórios',
  '/retencao': 'Retenção',
  '/nps': 'NPS',
  '/financeiro': 'Financeiro',
  '/agendamentos': 'Agendamentos',
  '/formularios': 'Formulários',
  '/ai-knowledge': 'Treinamento IA',
};

/**
 * Per-project-type config — build CRM (crm.blacklegacy.com.br) sempre usa
 * a configuração `crm` (única registrada).
 */
const PROJECT_TYPE_CONFIG = {
  crm: { icon: Kanban, label: 'CRM', home: '/pipeline' },
} as const;

/**
 * Best-effort label for routes not in ROUTE_LABELS — turns
 * "/foo-bar/baz" into "Baz" (last segment, prettified). Better than the
 * generic "Página" when a new route lands without a translation entry.
 */
function deriveRouteLabel(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'Página';
  const last = segments[segments.length - 1];
  // Skip UUID-ish segments (e.g. /lead/abc-123-...). When the last segment
  // looks like an id, fall back to the prior one when available.
  const looksLikeId = /^[\da-f]{6,}$/i.test(last) || /^\d+$/.test(last);
  const target = looksLikeId && segments.length >= 2 ? segments[segments.length - 2] : last;
  return target
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function Breadcrumbs() {
  const location = useLocation();
  const { activeProjectType, activeWorkspaceId, workspaces, activeProjectId } = useStore(useShallow((s) => ({
    activeProjectType: s.activeProjectType,
    activeWorkspaceId: s.activeWorkspaceId,
    workspaces: s.workspaces,
    activeProjectId: s.activeProjectId,
  })));

  const currentLabel = ROUTE_LABELS[location.pathname] || deriveRouteLabel(location.pathname);
  const sectionConfig = PROJECT_TYPE_CONFIG[activeProjectType as keyof typeof PROJECT_TYPE_CONFIG]
    ?? PROJECT_TYPE_CONFIG.crm;
  const SectionIcon = sectionConfig.icon;

  // Find workspace and project names
  const workspace = workspaces.find(w => w.id === activeWorkspaceId);
  const project = workspace?.projects?.find(p => p.id === activeProjectId);

  return (
    <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground">
      {workspace ? (
        <>
          <Link
            to="/workspaces"
            className="flex items-center gap-1 hover:text-foreground transition-colors"
            title={workspace.name}
          >
            <Building2 className="h-3 w-3" />
            <span className="max-w-[100px] lg:max-w-[160px] truncate">{workspace.name}</span>
          </Link>
          <ChevronRight className="h-3 w-3" />
          {project && (
            <>
              <span
                className="flex items-center gap-1 text-muted-foreground/70"
                title={project.name}
              >
                <SectionIcon className="h-3 w-3" />
                <span className="max-w-[100px] lg:max-w-[160px] truncate">{project.name}</span>
              </span>
              <ChevronRight className="h-3 w-3" />
            </>
          )}
        </>
      ) : (
        <>
          <Home className="h-3 w-3" />
          <ChevronRight className="h-3 w-3" />
        </>
      )}
      <Link
        to={sectionConfig.home}
        className="text-muted-foreground/70 hover:text-foreground transition-colors"
        title={`Ir para ${sectionConfig.label}`}
      >
        {sectionConfig.label}
      </Link>
      <ChevronRight className="h-3 w-3" />
      <span className="font-medium text-foreground">{currentLabel}</span>
    </div>
  );
}
